/**
 * EZ Naija Automated Communication Manager
 * Triggers messaging logs based on provider onboarding status changes
 */

export const NOTIFICATION_TEMPLATES = {
    'Submitted': {
        subject: 'Application Received - EZ Naija Onboarding',
        body: 'Dear {{provider_name}},\n\nThank you for applying to become a verified EZ Naija provider! Your application (ID: {{provider_id}}) has been successfully received.\n\nOur team is currently reviewing your credentials. You can track your real-time verification status on your dashboard: {{dashboard_link}}.\n\nIf you have any questions, please contact {{support_email}}.\n\nWarm regards,\nEZ Naija Onboarding\nonboarding@eznaijaapp.com',
        type: 'Application received'
    },
    'Under Review': {
        subject: 'Your Application is Under Review - EZ Naija',
        body: 'Dear {{provider_name}},\n\nYour application (ID: {{provider_id}}) is now under active review by our safety and verification team.\n\nIf we require any additional files, we will notify you immediately. You can check updates here: {{dashboard_link}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Under review'
    },
    'Awaiting Documents': {
        subject: 'Action Required: Missing Documents for Onboarding - EZ Naija',
        body: 'Dear {{provider_name}},\n\nPlease log in to your dashboard to view the requested files and upload them: {{upload_link}}.\n\nOur team is waiting on these documents to proceed with your application (ID: {{provider_id}}).\n\nIf you have questions, contact {{support_email}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Missing documents'
    },
    'Verification Pending': {
        subject: 'Verification Process Initiated - EZ Naija',
        body: 'Dear {{provider_name}},\n\nYour profile verification has been initiated. We are performing final manual checks. You will receive an update shortly.\n\nTrack status: {{dashboard_link}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Verification pending'
    },
    'Conditionally Approved': {
        subject: 'Conditional Approval Notice - EZ Naija',
        body: 'Dear {{provider_name}},\n\nGood news! Your application (ID: {{provider_id}}) has been conditionally approved.\n\nPlease log in to your dashboard to complete the remaining onboarding steps: {{dashboard_link}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Conditional approval'
    },
    'Approved': {
        subject: 'Congratulations! Your Application is Approved - EZ Naija',
        body: 'Dear {{provider_name}},\n\nWe are excited to let you know that your application (ID: {{provider_id}}) has been fully approved!\n\nOur administrator will publish your profile to the marketplace shortly. Check your status here: {{dashboard_link}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Approved'
    },
    'Rejected': {
        subject: 'Application Update - EZ Naija',
        body: 'Dear {{provider_name}},\n\nThank you for your interest in joining the EZ Naija network. After a careful review, we regret to inform you that your application (ID: {{provider_id}}) was not approved at this time.\n\nIf you believe this was an error or wish to appeal, please contact {{support_email}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Rejected'
    },
    'Live': {
        subject: 'Your Profile is Now LIVE! - EZ Naija',
        body: 'Dear {{provider_name}},\n\nYour provider profile is now officially LIVE on the EZ Naija marketplace!\n\nCustomers can now view your details and request your services. Visit your dashboard to manage bookings and check metrics: {{dashboard_link}}.\n\nCongratulations and welcome aboard!\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Profile live'
    },
    'Reminder': {
        subject: 'Reminder: Complete Your Onboarding - EZ Naija',
        body: 'Dear {{provider_name}},\n\nThis is a friendly reminder to complete your EZ Naija onboarding application (ID: {{provider_id}}).\n\nPlease log in and complete the steps: {{dashboard_link}}.\n\nWarm regards,\nEZ Naija Onboarding',
        type: 'Reminder to complete onboarding'
    }
};

export const renderTemplate = (text, variables) => {
    let rendered = text;
    Object.keys(variables).forEach(key => {
        rendered = rendered.replace(new RegExp(`{{${key}}}`, 'g'), variables[key]);
    });
    return rendered;
};

/**
 * Logs a status-triggered notification to the database or fallback local storage
 */
export const logStatusTriggeredMessage = async (supabase, provider, newStatus, adminId = null) => {
    const templateKeyMap = {
        'submitted': 'Submitted',
        'under_review': 'Under Review',
        'awaiting_documents': 'Awaiting Documents',
        'verification_pending': 'Verification Pending',
        'conditionally_approved': 'Conditionally Approved',
        'approved': 'Approved',
        'rejected': 'Rejected',
        'live': 'Live'
    };
    const templateKey = templateKeyMap[newStatus.toLowerCase()] || newStatus;
    const template = NOTIFICATION_TEMPLATES[templateKey];
    if (!template) return;

    const variables = {
        provider_name: provider.full_name || 'Provider',
        provider_id: provider.application_id || 'EZP-000000',
        application_status: newStatus,
        support_email: 'support@eznaijaapp.com',
        dashboard_link: `${window.location.origin}/provider-dashboard`,
        upload_link: `${window.location.origin}/provider-dashboard#uploads`
    };

    const subject = renderTemplate(template.subject, variables);
    const body = renderTemplate(template.body, variables);

    const messageData = {
        provider_id: provider.id,
        sender_email: 'onboarding@eznaijaapp.com',
        recipient_email: provider.email,
        subject: subject,
        message_body: body,
        message_type: template.type,
        status_trigger: newStatus,
        channel: 'Email',
        admin_id: adminId
    };

    console.log(`[Email Mock] Sending to ${provider.email} via onboarding@eznaijaapp.com:`, {
        subject,
        body
    });

    // Call the backend SMTP relay endpoint to send the real email
    try {
        console.log(`[SMTP Client] Requesting SMTP transmission via backend proxy to ${provider.email}...`);
        fetch('/api/send-email', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                recipient_email: provider.email,
                subject: subject,
                message_body: body
            })
        })
        .then(res => res.json())
        .then(result => {
            if (result.status === 'success') {
                console.log(`[SMTP Client] Real email dispatched successfully via SMTP messageId: ${result.messageId}`);
            } else {
                console.warn(`[SMTP Client] Backend SMTP send reported failure:`, result.message);
            }
        })
        .catch(err => {
            console.error(`[SMTP Client] Backend SMTP send fetch error:`, err);
        });
    } catch (err) {
        console.error(`[SMTP Client] Fetch connection to backend SMTP proxy failed:`, err);
    }

    try {
        // Try writing to Supabase
        const { data, error } = await supabase
            .from('provider_messages')
            .insert([messageData])
            .select();

        if (error) throw error;
        return { success: true, data };
    } catch (e) {
        console.warn("Supabase provider_messages insert failed. Falling back to localStorage logs.", e);
        
        // LocalStorage Fallback for communication log
        try {
            const savedLogs = localStorage.getItem('ez_provider_messages_fallback');
            const logs = savedLogs ? JSON.parse(savedLogs) : [];
            const newLog = {
                id: Math.random().toString(36).substring(2, 11),
                ...messageData,
                created_at: new Date().toISOString()
            };
            logs.unshift(newLog);
            localStorage.setItem('ez_provider_messages_fallback', JSON.stringify(logs));
            return { success: true, fallback: true, data: newLog };
        } catch (err) {
            console.error("Failed to write communication log to fallback localStorage", err);
            return { success: false, error: err };
        }
    }
};
